/* Shared native-offer transport, state and mutation lock. Shopify owns cart changes. */
if (!window.K9NativeOffer) {
  const state = { pending: false, version: 0, drafts: new Map(), name: '', phone: '' };
  window.K9PatchOfferState = state;
  const initializedOffers = () => Array.from(document.querySelectorAll('k9-patch-offer, k9-product-offer, k9-fixed-offer, k9-gift-offer, k9-bogo-offer, k9-paliers-offer')).filter(offer => offer.config);

  class Base extends HTMLElement {
    static items() { return null; }
    static validation() { return null; }
    get cartPatch() { return false; }
    nativeFields() { return {}; }
    synchronise() {}
    reflect() {}
    showFieldError() {}
    updatePresentation() {}

    static propertiesKey(properties) {
      return JSON.stringify(Object.entries(properties || {}).filter(([, value]) => value !== '')
        .sort(([a], [b]) => a.localeCompare(b)));
    }

    static quantity(cart, item) {
      return (cart.items || []).filter(line => String(line.id) === String(item.id)
        && this.propertiesKey(line.properties) === this.propertiesKey(item.properties))
        .reduce((sum, line) => sum + line.quantity, 0);
    }

    connectedCallback() {
      this.events?.abort();
      this.events = new AbortController();
      const options = { signal: this.events.signal };
      this.config = JSON.parse(this.querySelector('[data-offer-config]').textContent);
      this.mode = this.dataset.mode;
      // The same patch draft follows its drawer card between collar and harness eligibility.
      this.key = this.cartPatch ? `patch:${this.config.products.patch.id}:cart`
        : `${this.dataset.offerHandle || 'patch'}:${this.mode}:${this.dataset.pageProduct || ''}`;
      this.draft = state.drafts.get(this.key) || { choices: {}, open: this.mode === 'cart' && !this.cartPatch, blocked: false };
      state.drafts.set(this.key, this.draft);
      this.details = this.querySelector('.k9-patch-offer__options');
      this.button = this.querySelector('[data-submit]');
      this.label = this.querySelector('[data-button-label]');
      this.defaultLabel = this.label.textContent;
      for (const control of this.querySelectorAll('[data-variant]')) {
        const saved = this.draft.choices[control.dataset.variant];
        // Liquid supplies the current cart default until the visitor explicitly chooses a size.
        if (!saved || (this.cartPatch && !this.draft.manualPatch)) continue;
        // A choice is either a select or a group of radio buttons sharing the same data-variant.
        if (control.type === 'radio') { if (control.value === saved && !control.disabled) control.checked = true; }
        else if (Array.from(control.options).some(option => option.value === saved)) control.value = saved;
      }
      // A form reopened by a redraw must not pull keyboard focus (see k9-cart-drawer-ui.js).
      if (this.draft.open && !this.details.open && this.details.dataset) this.details.dataset.k9Restored = '';
      this.details.open = this.draft.open;
      const native = this.nativeFields();
      if (native.name?.value) state.name = native.name.value;
      if (native.phone?.value) state.phone = native.phone.value;
      for (const offer of document.querySelectorAll('k9-patch-offer')) if (offer.config) offer.reflect();
      this.addEventListener('input', event => {
        event.stopPropagation();
        const field = event.target.dataset.field;
        if (field) {
          state[field] = event.target.value;
          this.synchronise();
          this.showFieldError(field, '');
        }
      }, options);
      this.addEventListener('change', event => {
        // CartItems treats an unfiltered change as a quantity update. These are offer choices.
        event.stopPropagation();
        if (this.cartPatch && event.target.dataset.variant === 'patch') this.draft.manualPatch = true;
        this.update();
      }, options);
      this.addEventListener('keydown', event => {
        if (event.key === 'Enter' && event.target.matches('input')) {
          event.preventDefault();
          event.stopPropagation();
          if (!event.isComposing) this.submit();
        }
      }, options);
      this.details.addEventListener('toggle', () => { this.draft.open = this.details.open; }, options);
      this.button.addEventListener('click', () => this.submit(), options);
      document.addEventListener('input', event => {
        const fields = this.nativeFields();
        if (event.target === fields.name) state.name = event.target.value;
        else if (event.target === fields.phone) state.phone = event.target.value;
        else return;
        for (const offer of document.querySelectorAll('k9-patch-offer')) if (offer.config) offer.reflect();
      }, options);
      this.update();
      if (this.draft.message) this.message(this.draft.message, this.draft.blocked);
    }

    disconnectedCallback() { this.events?.abort(); }

    update() {
      for (const control of this.querySelectorAll('[data-variant]')) {
        if (control.type !== 'radio' || control.checked) this.draft.choices[control.dataset.variant] = control.value;
      }
      const items = this.constructor.items(this.config, this.mode, this.draft.choices, state.name, state.phone);
      for (const container of this.querySelectorAll('[data-product-image]')) {
        const key = container.dataset.productImage;
        const variant = this.config.products[key].variants.find(v => String(v.id) === this.draft.choices[key]);
        const image = container.querySelector('img');
        if (variant?.image && image) {
          image.removeAttribute('srcset');
          image.src = variant.image;
        }
      }
      this.updatePresentation();
      const total = this.querySelector('[data-total]');
      if (total && items) {
        const cents = items.reduce((sum, item) => sum + Object.values(this.config.products)
          .flatMap(p => p.variants).find(v => v.id === item.id).price, 0);
        total.textContent = new Intl.NumberFormat(this.config.locale, { style: 'currency', currency: this.config.currency }).format(cents / 100);
      }
      this.button.disabled = state.pending || !items || this.draft.blocked;
      this.label.textContent = this.addedShown ? this.config.text.addedButton
        : state.pending ? this.config.text.busy : this.defaultLabel;
      // A sold-out size stays unavailable once the shared lock is released.
      for (const control of this.querySelectorAll('input, select')) control.disabled = state.pending || control.hasAttribute?.('data-sold-out') === true;
      this.button.setAttribute('aria-disabled', String(this.button.disabled));
      if (!items) this.message(this.config.text.unavailable);
    }

    message(text, recovery = false) {
      this.draft.message = text;
      const status = this.querySelector('[data-status]');
      status.textContent = text;
      status.hidden = !text;
      if (this.cartPatch) status.classList.toggle('visually-hidden', text === this.config.text.added);
      this.querySelector('[data-cart-link]').hidden = !recovery;
    }

    static async json(url, options = {}) {
      const controller = new AbortController();
      const timer = setTimeout(() => controller.abort(), 20000);
      try {
        const response = await fetch(url, { ...options, signal: controller.signal, cache: 'no-store' });
        return { response, data: await response.json() };
      } finally { clearTimeout(timer); }
    }

    static cartIsBusy() {
      return Boolean(document.querySelector('.cart__items--disabled, gp-product-button [data-state="loading"], button[name="add"].loading, button[name="add"][aria-busy="true"]')) || document.querySelector('cart-drawer')?.undoPending
        || Array.from(document.querySelectorAll('.quantity__input')).some(input => input.value !== input.getAttribute('value'));
    }

    async redraw(cart, focusVariant) {
      const drawer = document.querySelector('cart-drawer');
      if (!drawer || typeof drawer.renderContents !== 'function') return false;
      const url = new URL(location.pathname, location.origin);
      url.searchParams.set('sections', 'cart-drawer,cart-icon-bubble');
      const { response, data: sections } = await Base.json(url.toString());
      if (!response.ok) return false;
      const sectionDocuments = {};
      for (const key of ['cart-drawer', 'cart-icon-bubble']) {
        if (typeof sections?.[key] !== 'string') return false;
        const doc = new DOMParser().parseFromString(sections[key], 'text/html');
        if (!doc.querySelector(key === 'cart-drawer' ? '#CartDrawer' : '.shopify-section')) return false;
        sectionDocuments[key] = doc;
      }
      drawer.setActiveElement?.(this.button);
      // The render frame must choose its focus target from the new empty/non-empty state.
      drawer.classList.toggle('is-empty', cart.item_count === 0);
      drawer.renderContents({ id: focusVariant, sections }, { sectionDocuments });
      await new Promise(resolve => requestAnimationFrame(() => requestAnimationFrame(resolve)));
      if (focusVariant) {
        const target = drawer.querySelector(`a.cart-item__name[href*="variant=${focusVariant}"]`)
          || drawer.querySelector(`[data-k9-variant="${focusVariant}"] .cart-item__name`);
        target?.focus();
      }
      return true;
    }

    validateAdded() { return true; }

    async submit() {
      if (state.pending || this.draft.blocked) return;
      if (this.mode === 'bundle' && !this.details.open && this.constructor.requiresOptions !== false) {
        this.details.open = true;
        this.querySelector('[data-variant]').focus();
        return;
      }
      this.showFieldError('name', '');
      this.showFieldError('phone', '');
      const invalid = this.constructor.validation(state.name, state.phone);
      if (invalid) {
        this.details.open = true;
        this.showFieldError(invalid, this.config.text[`${invalid}Error`]);
        this.querySelector(`[data-field="${invalid}"]`).focus();
        return;
      }
      const items = this.constructor.items(this.config, this.mode, this.draft.choices, state.name, state.phone);
      if (!items) { this.message(this.config.text.unavailable); return; }
      if (Base.cartIsBusy()) { this.message(this.config.text.wait); return; }
      state.pending = true;
      state.version++;
      this.setAttribute('aria-busy', 'true');
      this.label.textContent = this.config.text.busy;
      for (const offer of initializedOffers()) offer.update();
      state.name = state.name.trim();
      state.phone = state.phone.trim();
      this.synchronise();
      let posted = false;
      let confirmed = false;
      let after;
      try {
        const baseline = await Base.json(this.config.cartUrl);
        if (!baseline.response.ok || !Array.isArray(baseline.data.items)) throw new Error('baseline');
        posted = true;
        const result = await Base.json(this.config.addUrl, {
          method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ items }),
        });
        const current = await Base.json(this.config.cartUrl);
        if (!current.response.ok || !Array.isArray(current.data.items)) throw new Error('cart');
        after = current.data;
        confirmed = items.every(item => Base.quantity(after, item) === Base.quantity(baseline.data, item) + 1)
          && this.validateAdded(after, items);
        if (!confirmed) {
          const noneAdded = items.every(item => Base.quantity(after, item) === Base.quantity(baseline.data, item));
          this.draft.blocked = result.response.ok || !noneAdded;
          this.draft.message = this.config.text[this.draft.blocked ? 'uncertain' : 'rejected'];
        } else {
          this.draft.message = '';
          // Lot 4: anonymous acceptance count (k9-offre-mesure.js), only after full confirmation.
          (window.K9MesureFile ||= []).push({ element: this, total: baseline.data.total_price, currency: baseline.data.currency });
        }
      } catch {
        this.draft.blocked = posted;
        this.draft.message = this.config.text[posted ? 'uncertain' : 'beforeError'];
        // Even 422 or a lost response can have changed the cart. No automatic retry or rollback.
        if (posted) {
          try {
            const current = await Base.json(this.config.cartUrl);
            if (current.response.ok && Array.isArray(current.data.items)) after = current.data;
          } catch { /* The recovery link opens a fresh cart when its state cannot be read. */ }
        }
      }
      if (this.cartPatch && posted && document.querySelector('cart-items')) {
        // The main cart still owns its old line indices. Keep the shared lock until
        // navigation; redrawing only its drawer must never release those indices.
        for (const offer of initializedOffers()) offer.update();
        if (confirmed && after) {
          try { location.reload(); return; }
          catch { /* Keep the old document locked and offer explicit recovery. */ }
        }
        this.draft.blocked = true;
        this.message(this.config.text.uncertain, true);
        const error = document.getElementById('CartDrawer-CartErrors');
        if (error) {
          error.textContent = this.config.text.uncertain;
          error.hidden = false;
          error.tabIndex = -1;
          const recovery = this.querySelector('[data-cart-link]')?.cloneNode(true);
          if (recovery) { recovery.hidden = false; error.append(recovery); }
          error.focus();
        } else this.querySelector('[data-status]')?.focus();
        return;
      }
      try {
        let rendered = false;
        // Brief confirmation on the drawer button, then the redraw removes the accepted offer.
        if (confirmed && after && this.cartPatch && this.config.text.addedButton) {
          this.addedShown = true; this.button.classList.add('is-added'); this.update();
          await new Promise(resolve => setTimeout(resolve, 900));
        }
        if (after) rendered = await this.redraw(after, confirmed ? items[items.length - 1].id : null);
        if (!rendered && confirmed) this.draft.message = this.config.text.added;
        const current = initializedOffers().find(offer => offer.key === this.key);
        if (current) current.message(this.draft.message || '', this.draft.blocked || (confirmed && !rendered));
        if (!confirmed) {
          const error = current?.querySelector('[data-status]') || document.getElementById('CartDrawer-CartErrors');
          if (error) { error.textContent = this.draft.message; error.hidden = false; error.focus(); }
        }
      } catch {
        this.message(confirmed ? this.config.text.added : this.draft.message, true);
      } finally {
        if (this.addedShown) { this.addedShown = false; this.button.classList.remove('is-added'); }
        state.pending = false;
        state.version++;
        this.removeAttribute('aria-busy');
        this.label.textContent = this.defaultLabel;
        for (const offer of initializedOffers()) offer.update();
      }
    }
  }

  // Lot 6b-1 (decision 3): the displayed rate comes from the Shopify discount, read in the Storefront quote on an
  // automatic percentage allocation. A float within 1e-9 of an integer (0.29 × 100) is that integer, as in
  // tools/k9 offres-coherence (spec § 7.2). Fixed amount, code or missing value: rate-absent; 0, 100, decimal: rate-invalid.
  const rateOf = allocation => {
    const value = allocation?.discountApplication?.value;
    if (allocation?.__typename !== 'CartAutomaticDiscountAllocation' || value?.__typename !== 'PricingPercentageValue'
      || value.percentage === undefined || value.percentage === null) throw new Error('rate-absent');
    const { percentage } = value, rate = Math.round(percentage);
    if (!Number.isFinite(percentage) || Math.abs(percentage - rate) >= 1e-9 || rate < 1 || rate > 99) throw new Error('rate-invalid');
    return rate;
  };

  // Both product duos use the same verified cart request and mutation lock; both engines read rateOf here.
  window.K9NativeOffer = { Base, state, rateOf };

  // Prevent overlapping UI mutations and checkout during the verified offer request.
  // CartItems' in-flight changes and debounced quantity edits are checked before it starts.
  const cartAction = 'cart-remove-button, .quantity__button, .quantity__input, #CartDrawer-Undo button, #CartDrawer-Checkout, gp-product-button button, button[name="add"]';
  document.addEventListener('click', event => {
    if (state.pending && event.target.closest(cartAction)) { event.preventDefault(); event.stopImmediatePropagation(); }
  }, true);
  document.addEventListener('submit', event => {
    if (state.pending && event.target.matches('form[action*="/cart"]')) { event.preventDefault(); event.stopImmediatePropagation(); }
  }, true);
  document.addEventListener('change', event => {
    if (state.pending && event.target.matches('.quantity__input')) { event.preventDefault(); event.stopImmediatePropagation(); }
  }, true);
}
